import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Edit2,
  ExternalLink,
  EyeOff,
  Loader2,
  Trash2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { adminApi } from "../utils/admin-api-axios";

const ResourcePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState(null);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const fetchResource = async () => {
    try {
      const data = await adminApi.getResource(id);
      if (data.success) {
        setResource(data.resource);
      } else {
        showMessage("error", data.message || "Could not load resource");
      }
    } catch (error) {
      showMessage("error", error.message || "Could not load resource");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePublish = async () => {
    if (!window.confirm("Publish this resource? It will appear on the public Resources page.")) {
      return;
    }

    setActionLoading(true);
    try {
      const data = await adminApi.publishResource(id);
      if (data.success) {
        setResource(data.resource);
        showMessage("success", "Resource published");
      } else {
        showMessage("error", data.message || "Failed to publish");
      }
    } catch (error) {
      showMessage("error", error.message || "Failed to publish");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm("Unpublish this resource? It will be removed from the public Resources page but kept as a draft.")) {
      return;
    }

    setActionLoading(true);
    try {
      const data = await adminApi.unpublishResource(id);
      if (data.success) {
        setResource(data.resource);
        showMessage("success", "Resource unpublished");
      } else {
        showMessage("error", data.message || "Failed to unpublish");
      }
    } catch (error) {
      showMessage("error", error.message || "Failed to unpublish");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = () => {
    // Navigate to manage page with edit query param — manage page will pick this up
    navigate(`/admin/resources?edit=${id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this resource? This can't be undone.")) return;

    setActionLoading(true);
    try {
      const data = await adminApi.deleteResource(id);
      if (data.success) {
        navigate("/admin/resources");
      } else {
        showMessage("error", data.message || "Could not delete");
      }
    } catch (error) {
      showMessage("error", error.message || "Could not delete");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/resources")}
          className="flex items-center text-sm text-gray-600 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Resources
        </button>
        <p className="text-gray-500">Resource not found.</p>
      </div>
    );
  }

  const isPublished = resource.status === "published";
  const hasLongExcerpt = resource.excerpt && resource.excerpt.length > 220;
  const displayExcerpt =
    hasLongExcerpt && !expanded
      ? resource.excerpt.slice(0, 220) + "..."
      : resource.excerpt;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/resources")}
            className="flex items-center text-sm text-gray-600 hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <span
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              isPublished
                ? "bg-primary/10 text-primary"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {isPublished ? "Published" : "Draft"}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleEdit}
            disabled={actionLoading}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors disabled:opacity-50"
          >
            <Edit2 className="w-4 h-4 mr-1.5" />
            Edit
          </button>
          {isPublished ? (
            <button
              onClick={handleUnpublish}
              disabled={actionLoading}
              className="flex items-center px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors disabled:opacity-50"
            >
              <EyeOff className="w-4 h-4 mr-1.5" />
              Unpublish
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={actionLoading}
              className="flex items-center px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-md transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Publish
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={actionLoading}
            className="flex items-center px-3 py-2 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-sm font-medium rounded-md transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Delete
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`px-4 py-3 rounded-md text-sm ${
            message.type === "success"
              ? "bg-primary/10 text-primary border border-primary/20"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Preview heading */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Preview</h2>
        <p className="text-sm text-gray-500 mt-1">
          This is exactly how the resource will appear on the public page.
        </p>
      </div>

      {/* The actual preview — styled identically to the public Resources page card */}
      <div className="bg-gray-50 rounded-2xl p-6 md:p-10">
        <div className="max-w-xl mx-auto">
          <div className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col">
            {resource.imageUrl && (
              <div className="w-full bg-muted flex items-center justify-center overflow-hidden max-h-96">
                <img
                  src={resource.imageUrl}
                  alt={resource.sourceName}
                  className="w-full h-auto max-h-96 object-contain"
                />
              </div>
            )}

            <div className="p-6 md:p-8 flex flex-col flex-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                {resource.sourceName}
              </div>

              {resource.title && (
                <h3 className="text-2xl font-bold text-foreground mb-3 leading-tight">
                  {resource.title}
                </h3>
              )}

              <p className="text-foreground/80 leading-relaxed mb-4">
                {resource.blurb}
              </p>

              {resource.excerpt && (
                <blockquote className="border-l-4 border-primary/40 bg-primary/5 pl-4 pr-4 py-3 my-2 text-muted-foreground italic text-sm leading-relaxed rounded-r-md">
                  {displayExcerpt}
                  {hasLongExcerpt && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setExpanded(!expanded);
                      }}
                      className="not-italic font-semibold text-primary hover:text-primary/80 ml-2 underline"
                    >
                      {expanded ? "show less" : "read more"}
                    </button>
                  )}
                </blockquote>
              )}

              <div className="mt-auto pt-4 flex items-center text-sm font-semibold text-primary">
                Visit source
                <ExternalLink className="w-4 h-4 ml-1.5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Meta info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-2 text-sm">
            <div className="flex flex-wrap">
              <dt className="text-gray-500 w-32 flex-shrink-0">Source URL:</dt>
              <dd className="text-gray-800 flex-1 min-w-0 break-all">
                <a
                  href={resource.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary inline-flex items-center"
                >
                  {resource.sourceUrl}
                  <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
                </a>
              </dd>
            </div>
            <div className="flex flex-wrap">
              <dt className="text-gray-500 w-32 flex-shrink-0">Created:</dt>
              <dd className="text-gray-800">
                {new Date(resource.createdAt).toLocaleString()}
              </dd>
            </div>
            <div className="flex flex-wrap">
              <dt className="text-gray-500 w-32 flex-shrink-0">Last updated:</dt>
              <dd className="text-gray-800">
                {new Date(resource.updatedAt).toLocaleString()}
              </dd>
            </div>
            {resource.publishedAt && (
              <div className="flex flex-wrap">
                <dt className="text-gray-500 w-32 flex-shrink-0">
                  {isPublished ? "Published:" : "Last published:"}
                </dt>
                <dd className="text-gray-800">
                  {new Date(resource.publishedAt).toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResourcePreview;